/**
 * What each app does for the person, in plain words.
 *
 * Someone setting up for the first time picks what they want done, not which
 * product does it, so onboarding leads with the benefit. App names come in
 * gently: as small secondary labels on the outcomes step, and properly on the
 * last step, each beside its benefit.
 *
 * Every phrase is written for a non-technical reader and never names a
 * product. Keyed by registry appId; every app an outcome can switch on has an
 * entry (see backend focusRegistry).
 */

export type BenefitMode = 'personal' | 'business';

/** appId → the benefit, in plain words. */
export const APP_BENEFITS: Readonly<Record<string, string>> = {
  home: 'Family calendar, chores and budget',
  legacy: 'Important papers and family wealth in one place',
  scheduler: 'Your days, bookings and reminders in one calendar',
  marketplace: 'A profile the right people can find',
  beheard: 'Practice and grow your expertise',
  crm: 'Every customer and deal in one place',
  outbound: 'Reach new customers by text, email and calls',
  vitals: 'A clear marketing plan and posting schedule',
  brandtalk: 'A brand that sounds like you everywhere',
  weopen: 'A website assistant that answers visitors',
  pitchless: 'Pitch with confidence',
  patchproof: 'Know your systems are safe',
  reconcile: 'Know your numbers every day',
  flows: 'Busywork that runs itself',
  practice: 'Patient records and appointments in one place',
};

/**
 * The few apps that do a different job for a business. Legacy and BeHeard
 * also carry business copy in the registry; their personal phrases would
 * misdescribe them on "Build a Stronger Business" or "Train Your Team".
 */
const BUSINESS_BENEFITS: Readonly<Record<string, string>> = {
  legacy: 'Important papers and succession plans in one place',
  beheard: 'Train your team and grow their expertise',
};

/** The most benefits one outcome card lists. */
export const MAX_CARD_BENEFITS = 3;

export interface BenefitSource {
  appId: string;
  displayName?: string;
  /** Registry copy from the API — the fallback for an app with no entry. */
  subtitle?: string | null;
  description?: string | null;
}

const has = (map: Readonly<Record<string, string>>, key: string) =>
  Object.prototype.hasOwnProperty.call(map, key);

/**
 * The plain benefit of one app. An app with no entry falls back to its
 * registry subtitle, then its description. It never falls back to a name: a
 * fallback that is only the app's name (or id) is skipped, and an app with
 * nothing else to say returns '' so callers can leave the line out.
 */
export function appBenefit(app: BenefitSource, mode?: BenefitMode | null): string {
  if (mode === 'business' && has(BUSINESS_BENEFITS, app.appId)) return BUSINESS_BENEFITS[app.appId];
  if (has(APP_BENEFITS, app.appId)) return APP_BENEFITS[app.appId];

  const names = [app.appId, app.displayName ?? '']
    .map((n) => n.trim().toLowerCase())
    .filter(Boolean);
  for (const copy of [app.subtitle, app.description]) {
    const text = typeof copy === 'string' ? copy.trim() : '';
    if (text && !names.includes(text.toLowerCase())) return text;
  }
  return '';
}

/**
 * An outcome card's bullets: the benefits of its apps in the outcome's own app
 * order, each phrase once, at most `max`, and never an empty one.
 */
export function outcomeBenefits(
  apps: readonly BenefitSource[],
  mode?: BenefitMode | null,
  max: number = MAX_CARD_BENEFITS,
): string[] {
  const seen = new Set<string>();
  const benefits: string[] = [];
  for (const app of apps) {
    if (benefits.length >= max) break;
    const benefit = appBenefit(app, mode);
    const key = benefit.toLowerCase();
    if (!benefit || seen.has(key)) continue;
    seen.add(key);
    benefits.push(benefit);
  }
  return benefits;
}
